import { useRef, useState, useCallback } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import Toolbar from '../components/page-toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { appStore } from '../stores/app-store';
import { Window } from '@progress/kendo-react-dialogs';
import { Error, Label } from '@progress/kendo-react-labels';
import LanguageComboBox from '../components/combobox/language-combobox';
import { StandardInput } from '../components/forms';
import { Button } from '@progress/kendo-react-buttons';
import {
  AdminAddDisplayLanguageAsync,
  AdminAddDisplayLanguageDocumentAsync,
  AdminUpdateDisplayLanguageContentAsync
} from '../services/admin-display-language';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/toast-context';
import { useGetAllAdminDisplayLanguage } from '../hooks/services/use-languages';
import { Loader } from '@progress/kendo-react-indicators';
import { useGetLanguages } from '../hooks/services/use-countries';

const initialDataState: State = {
  sort: [{ field: 'languageName', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const LanguagesManagementGrid = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');

  const [addLanguage, setAddLanguage] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [languageDescription, setLanguageDescription] = useState<string>('');
  const gridExportRef = useRef<ExcelExport | null>(null);
  const [isLanguageDuplicate, setIsLanguageDuplicate] = useState(false);
  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetAllAdminDisplayLanguage(
    toDataSourceRequestString(dataState)
  );

  const { languages } = useGetLanguages();

  const onColumnTextClick = (props: GridCellProps) => (
    <EditCommandCell {...props} />
  );

  const handleTitleClick = (id: number) => {
    navigate('/administration/languages/manage/' + id);
  };

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer'}
          onClick={() => handleTitleClick(props.dataItem.id)}>
          {props.dataItem.languageName}
        </span>
      </td>
    );
  };

  const onSubmit = useCallback(async () => {
    if (!selectedLanguage) return;

    try {
      appStore.showLoading();

      const countryISO = (languages ?? []).find(
        (x) => x.language === selectedLanguage
      )?.countryIso;

      if (!countryISO) {
        onToastOpen({
          title: 'Display language',
          body: <div>Language code not found for the selected language.</div>,
          style: 'error'
        });
        return;
      }

      const language = await AdminAddDisplayLanguageAsync({
        languageCode: countryISO,
        description: languageDescription
      });

      const document = await AdminAddDisplayLanguageDocumentAsync(language.id, {
        documentStatus: 'New',
        version: 'v.0.0.1',
        fileName: `display-language-${language.id}-v.0.0.1.json`
      });

      await AdminUpdateDisplayLanguageContentAsync(language.id, document.id, {
        languageCode: countryISO,
        description: languageDescription,
        resources: []
      });

      setAddLanguage(false);
      setSelectedLanguage('');
      setLanguageDescription('');
      setIsLanguageDuplicate(false);
      onToastOpen({
        title: 'Display language',
        body: <div>Language added successfully.</div>,
        style: 'success'
      });

      mutate();
    } catch (error) {
      onToastOpen({
        title: 'Display language',
        body: <div>Failed to add language.</div>,
        style: 'error'
      });
      console.error('Failed to add language:', error);
    } finally {
      appStore.hideLoading();
    }
  }, [selectedLanguage, languageDescription, languages, mutate, onToastOpen]);

  const handleDataStateChange = (
    e: GridDataStateChangeEvent | GridToolbarDataStateChangeEvent
  ) => {
    const newState = {
      ...e.dataState,
      filter: {
        logic: 'and',
        filters: [e.dataState.filter!]
      }
    } as State;
    setDataState(newState);
  };

  return (
    <>
      <Toolbar title="Global Languages">
        <Button onClick={() => setAddLanguage(true)} themeColor="primary">
          Add new
        </Button>
      </Toolbar>
      <div
        className="pt-2"
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          showCardMode={false}
          searchPlaceholder="Search global languages"
          columnsToSearch={['languageName', 'description']}
          exportRef={gridExportRef}
          onGridModeChange={(e) => setGridMode(e.gridMode)}
          {...dataState}
          onDataStateChange={handleDataStateChange}
        />
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName="libraries.xlsx">
          {gridMode === 'grid' && (
            <Grid
              id="grid-library"
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              pageable={DefaultGridSettings.pagerSettings}
              sortable
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}>
              <Column
                field="languageName"
                title="Title"
                cells={{ data: onColumnTextClick }}
              />
              <Column
                field="description"
                title="Description"
                columnMenu={ColumnMenu}
              />
              <Column
                field="createdDateUtc"
                title="Date Created"
                format="{0:g}"
              />
              <Column field="version" title="Version" />
              <Column field="status" title="Status" />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
        </ExcelExport>
      </div>
      {addLanguage && (
        <Window
          width={400}
          onClose={() => {
            setAddLanguage(false);
            setSelectedLanguage('');
            setLanguageDescription('');
            setIsLanguageDuplicate(false);
          }}
          title="New global languages">
          <div>
            <Label>Language title</Label>
            <LanguageComboBox
              name="language"
              onChange={(v) => {
                const value: string = v?.target?.value ?? '';
                const findExistingLanguage = data?.data
                  ?.map((item) => item.languageName)
                  ?.find((i: string) => i?.toLowerCase() === value?.toLowerCase());

                if (!findExistingLanguage) {
                  setIsLanguageDuplicate(false);
                } else {
                  setIsLanguageDuplicate(true);
                }
                setSelectedLanguage(value);
              }}
            />
            {isLanguageDuplicate && (
              <div className={'mt-1'}>
                <Error>
                  The selected language has already been added, please choose
                  another.
                </Error>
              </div>
            )}
          </div>
          <div>
            <StandardInput
              name={'description'}
              label={'Description'}
              onChange={(e) =>
                setLanguageDescription((e?.target?.value as string) ?? '')
              }
            />
          </div>
          <div className={'d-flex justify-content-end mt-2'}>
            <Button
              disabled={isLanguageDuplicate || !selectedLanguage}
              onClick={onSubmit}
              themeColor={'primary'}>
              Confirm
            </Button>
          </div>
        </Window>
      )}
    </>
  );
};

export default LanguagesManagementGrid;
